import { DialogService } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Prompt } from 'elements/prompt';
import { AsyncErrorHandler } from 'lib/ui';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { FileAttachmentService } from 'services/file-attachment-service';
import { NetDimensionService } from 'services/net-dimension-service';
import { NetService } from 'services/net-service';
import { ServiceService } from 'services/service-service';
import { SpecialProductDimensionService } from 'services/special-product-dimension-service';
import { ToastService } from 'services/toast-service';

@autoinject
export class ServiceDetailEmergencyRepair {
  @bindable private serviceId;
  @bindable private netId;
  @bindable private isDialog: boolean = false;

  private nextTabIndex: number = null;
  private hasDocumentation: boolean = false;

  private net: Models.Net;
  private netdimension: Models.NetDimension;
  private specialProductDimension: Models.SpecialProductDimension;
  private service: Models.Service;
  private isLocked: boolean;

  private subscription: Subscription;

  constructor(
    private eventAggregator: EventAggregator,
    private fileAttachmentService: FileAttachmentService,
    private serviceService: ServiceService,
    private netService: NetService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private i18n: I18N,
    private dialogService: DialogService,
    private netDimensionService: NetDimensionService,
    private specialProductDimensionService: SpecialProductDimensionService
  ) {}

  private async attached() {
    if (this.serviceId && this.netId) {
      await this.activate({ Id: this.serviceId, NetId: this.netId });
    }
  }

  private async activate(params: { Id: number; NetId: number }) {
    this.serviceId = params.Id;
    this.netId = params.NetId;

    await this.getService(params.Id);
    await this.getNet(params.NetId);

    // Get new tabIndex for tabs component via EA, store value so we can publish this if canDeactivate returns true
    this.eventAggregator.subscribe('changeTab', (tabIndex) => {
      this.nextTabIndex = tabIndex;
    });

    this.subscription = this.eventAggregator.subscribe('service-unlocked', async (serviceId) => {
      if (this.service.Id === serviceId) {
        await this.getService(this.service.Id);
      }
    });
  }

  deactivate() {
    if (this.subscription) {
      this.subscription.dispose();
    }
  }

  @AsyncErrorHandler
  async getNet(id: number) {
    const response = await this.netService.getNetInfo(id);
    this.net = response;
    await this.getDimensions(this.net);
  }

  @AsyncErrorHandler
  async getDimensions(net: Models.Net) {
    if (net.NetDimensionId) {
      const response = await this.netDimensionService.get(net.NetDimensionId + '?$expand=DimensionClass');
      this.netdimension = response;
    } else if (net.SpecialProductDimensionId) {
      const response = await this.specialProductDimensionService.get(net.SpecialProductDimensionId);
      this.specialProductDimension = response;
    }
  }

  @AsyncErrorHandler
  async getService(id: number) {
    const response = await this.serviceService.get(id + '?$expand=NetStandard');
    this.service = response;

    this.isLocked = response.Locked ? response.Locked : false;
  }

  @AsyncErrorHandler
  async hasFiles() {
    this.fileAttachmentService.setApiUrl('api/services');
    const files = (await this.fileAttachmentService.getFiles(this.service.Id)) as File[];
    return files.length > 0 ? true : false;
  }

  @AsyncErrorHandler
  async saveComment() {
    await this.serviceService.patchValue(this.serviceId, 'CommentExtendedPeriod', this.service.CommentExtendedPeriod);
    this.toastService.showSuccess('counter.commentUpdated');
  }

  private async approveAndFinish() {
    if (!(await this.hasFiles())) {
      this.toastService.showWarning('service.uploadEmergencyRepairDocumentationWarning');
      return;
    }

    return this.dialogService
      .open({
        viewModel: Prompt,
        model: {
          header: 'service.emergencyRepair',
          message: this.i18n.tr('dialog.serviceApproveEmergencyRepairText'),
          actions: {
            delete: { enabled: false },
            save: { enabled: false },
            cancel: { enabled: true, t: 'dialog.cancel' },
            dontsave: { enabled: false },
            continue: { enabled: true, t: 'dialog.serviceApproveAndExtend' },
          },
        },
      })
      .whenClosed((response) => {
        if (response.wasCancelled) {
          return false;
        } else {
          const result = response.output;
          if (result === 'continue') {
            this.serviceService
              .approveEmergencyRepair(this.service.Id)
              .then(async () => {
                this.toastService.showSuccess('service.approveEmergencyRepair');
                this.eventAggregator.publish('serviceLocked');
                await this.getService(this.service.Id);
              })
              .catch((err) => this.errorService.handleError(err));

            return false;
          } else {
            return true;
          }
        }
      });
  }

  private dontApprove() {
    return this.dialogService
      .open({
        viewModel: Prompt,
        model: {
          header: 'dialog.serviceDontApproveEmergencyRepairHeader',
          message: 'dialog.serviceDontApproveEmergencyRepairText',
          actions: {
            delete: { enabled: false },
            save: { enabled: false },
            cancel: { enabled: true, t: 'dialog.cancel' },
            dontsave: { enabled: false },
            continue: { enabled: true, t: 'dialog.serviceDontApproveEmergencyRepair' },
          },
        },
      })
      .whenClosed((response) => {
        if (response.wasCancelled) {
          return false;
        } else {
          const result = response.output;
          if (result === 'continue') {
            this.serviceService
              .dontApproveEmergencyRepair(this.service.Id)
              .then(async () => {
                this.toastService.showSuccess('service.emergencyRepairNotApproved');
                this.eventAggregator.publish('serviceLocked');
                await this.getService(this.service.Id);
              })
              .catch((err) => this.errorService.handleError(err));
            return false;
          } else {
            return true;
          }
        }
      });
  }

  public canDeactivate() {
    this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
    return true;
  }
}
